import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Button,
  VStack,
  Text,
  Input,
  FormControl,
  FormLabel,
  useToast,
  useBreakpointValue
} from '@chakra-ui/react'
import { useState } from 'react'
import { setNickname } from '../../services/apiService'

interface NicknameModalProps {
  isOpen: boolean
  onClose: () => void
  onSuccess: () => void
}

const NicknameModal = ({ isOpen, onClose, onSuccess }: NicknameModalProps) => {
  const [nickname, setNicknameValue] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const toast = useToast()
  const isMobile = useBreakpointValue({ base: true, md: false })

  const handleSubmit = async () => {
    if (!nickname.trim()) {
      toast({
        title: '닉네임을 입력해주세요',
        status: 'warning',
        duration: 3000,
        isClosable: true
      })
      return
    }

    if (nickname.length < 2) {
      toast({
        title: '닉네임은 최소 2자 이상이어야 합니다',
        status: 'warning',
        duration: 3000,
        isClosable: true
      })
      return
    }

    if (nickname.length > 20) {
      toast({
        title: '닉네임은 최대 20자까지 가능합니다',
        status: 'warning',
        duration: 3000,
        isClosable: true
      })
      return
    }

    try {
      setSubmitting(true)
      await setNickname(nickname)
      
      toast({
        title: '닉네임 설정 완료',
        description: '커뮤니티에서 사용할 닉네임이 설정되었습니다.',
        status: 'success',
        duration: 3000,
        isClosable: true
      })
      
      onSuccess()
      onClose()
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || '닉네임 설정에 실패했습니다.'
      
      // 중복 닉네임 에러인 경우 특별 처리
      if (errorMessage.includes('이미 사용 중인 닉네임')) {
        toast({
          title: '닉네임 중복',
          description: '이미 사용 중인 닉네임입니다. 다른 닉네임을 입력해주세요.',
          status: 'error',
          duration: 4000,
          isClosable: true
        })
      } else if (errorMessage.includes('변경할 수 없습니다')) {
        toast({
          title: '닉네임 변경 불가',
          description: '닉네임은 한 번 설정하면 변경할 수 없습니다.',
          status: 'error',
          duration: 4000,
          isClosable: true
        })
      } else {
        toast({
          title: '닉네임 설정 실패',
          description: errorMessage,
          status: 'error',
          duration: 3000,
          isClosable: true
        })
      }
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size={isMobile ? "sm" : "md"}
      isCentered
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader textAlign="center" pb={2}>
          🎭 커뮤니티 닉네임 설정
        </ModalHeader>
        <ModalCloseButton />
        
        <ModalBody pb={6}>
          <VStack spacing={4}>
            <Text 
              textAlign="center" 
              color="gray.600" 
              fontSize="sm"
              lineHeight="1.5"
            >
              커뮤니티에서 사용할 닉네임을 설정해주세요.<br />
              <Text as="span" color="red.500" fontWeight="bold">
                한 번 설정하면 변경할 수 없습니다.
              </Text>
            </Text>
            
            <FormControl>
              <FormLabel fontSize="sm" fontWeight="medium">
                닉네임
              </FormLabel>
              <Input
                value={nickname}
                onChange={(e) => setNicknameValue(e.target.value)}
                placeholder="닉네임을 입력하세요 (2-20자)"
                maxLength={20}
                borderColor="blue.300"
                _focus={{ 
                  borderColor: 'blue.500', 
                  boxShadow: '0 0 0 1px #3182ce' 
                }}
              />
              <Text fontSize="xs" color="gray.500" mt={1}>
                {nickname.length}/20자
              </Text>
            </FormControl>

            <VStack spacing={2} w="100%" pt={2}>
              <Button
                bg="linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
                color="white"
                onClick={handleSubmit}
                isLoading={submitting}
                loadingText="설정 중..."
                size="md"
                w="100%"
                borderRadius="lg"
                boxShadow="0 4px 15px rgba(102, 126, 234, 0.3)"
                _hover={{
                  transform: 'translateY(-1px)',
                  boxShadow: '0 6px 20px rgba(102, 126, 234, 0.4)',
                  bg: 'linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%)'
                }}
                _active={{
                  transform: 'translateY(0)',
                  boxShadow: '0 2px 10px rgba(102, 126, 234, 0.3)'
                }}
                transition="all 0.2s cubic-bezier(0.4, 0, 0.2, 1)"
              >
                ✨ 닉네임 설정하기
              </Button>
              
              <Text fontSize="xs" color="gray.400" textAlign="center">
                * 닉네임은 커뮤니티에서만 사용됩니다
              </Text>
            </VStack>
          </VStack>
        </ModalBody>
      </ModalContent>
    </Modal>
  )
}

export default NicknameModal
